import { useEffect, useState } from "react";
import { Star, Quote } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { TestimonialForm } from "@/components/forms/TestimonialForm";
import { api } from "@/lib/api";
import { ASSETS } from "@/lib/constants";
import { useSEO } from "@/hooks/useSEO";

function Stars({ n = 5 }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={15} className={i <= n ? "fill-blaze-orange text-blaze-orange" : "text-white/20"} />
      ))}
    </div>
  );
}

export default function TestimonialsPage() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useSEO({
    title: "Testimonials | CT BLAZERS AAU Basketball Norwalk, CT",
    description:
      "Hear from CT BLAZERS families and players. Share your own experience with our AAU basketball program in Norwalk, CT.",
    keywords: "CT Blazers reviews, CT Blazers testimonials, AAU Basketball Norwalk CT",
    path: "/testimonials",
  });

  const load = () =>
    api
      .get("/testimonials")
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]))
      .finally(() => setLoaded(true));

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="bg-ink-900 min-h-screen">
      <Navbar />
      <PageHeader
        eyebrow="Blazer Families"
        title="Testimonials"
        image={ASSETS.girlsTeam}
        subtitle="Real words from the CT BLAZERS community. Have a story to share? We'd love to hear it."
      />

      <section className="py-16 sm:py-24 bg-ink-800">
        <div className="container-x">
          <div className="grid gap-10 lg:grid-cols-5">
            <div className="lg:col-span-3">
              <Reveal>
                <h2 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl text-white">
                  What People Say
                </h2>
              </Reveal>
              {loaded && items.length === 0 ? (
                <Reveal>
                  <div
                    data-testid="testimonials-page-empty"
                    className="mt-6 rounded-2xl border border-dashed border-white/15 bg-ink-700/50 p-10 text-center"
                  >
                    <Quote className="mx-auto text-blaze-orange" size={40} />
                    <p className="mt-4 text-white/70">No testimonials yet — share yours using the form and it'll appear here once approved.</p>
                  </div>
                </Reveal>
              ) : (
                <div className="mt-6 space-y-5">
                  {items.map((t, i) => (
                    <Reveal key={t.id} delay={(i % 4) * 0.06}>
                      <figure
                        data-testid={`page-testimonial-${i}`}
                        className="rounded-2xl border border-white/10 bg-ink-700 p-6 hover:border-blaze-orange/40 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <Quote className="text-blaze-blueLight" size={26} />
                          <Stars n={t.rating} />
                        </div>
                        <blockquote className="mt-3 text-white/80 leading-relaxed">"{t.message}"</blockquote>
                        <figcaption className="mt-4">
                          <span className="font-heading text-xl tracking-wide text-white">{t.name}</span>
                          {t.role && <span className="text-sm text-white/50"> · {t.role}</span>}
                        </figcaption>
                      </figure>
                    </Reveal>
                  ))}
                </div>
              )}
            </div>

            <div className="lg:col-span-2">
              <Reveal delay={0.1}>
                <div className="rounded-2xl border border-white/10 bg-ink-700/70 p-6 sm:p-8 shadow-2xl sticky top-24">
                  <h3 className="font-heading text-3xl tracking-wide text-white">Share Your Experience</h3>
                  <p className="mt-2 text-sm text-white/60">Submissions are reviewed before appearing on the site.</p>
                  <div className="mt-5">
                    <TestimonialForm onSuccess={load} />
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
